import React, { useState, useEffect, useRef } from 'react';
import { Calendar, MapPin, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// Cloudinary transformation function
const transformUrl = (url, size = 'default') => {
  const baseUrl = url.split('/upload/')[0];
  const imageId = url.split('/upload/')[1];

  const transforms = {
    default: 'f_auto,q_auto,w_1200,c_fill',
    thumbnail: 'w_50,e_blur:1000,q_1,f_auto'
  };

  return `${baseUrl}/upload/${transforms[size]}/${imageId}`;
};

const Intro = () => {
  const { t } = useTranslation();
  const [activeImage, setActiveImage] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const galleryImages = [
    {
      src: "https://res.cloudinary.com/dx5y2bzdq/image/upload/f_auto,q_auto,w_800/v1741779867/184-sur-355_bqysac.jpg",
    },
    {
      src: "https://res.cloudinary.com/dx5y2bzdq/image/upload/f_auto,q_auto,w_800/v1741779867/290-sur-355_l5fk2q.jpg"
    },
    {
      src: "https://res.cloudinary.com/dx5y2bzdq/image/upload/f_auto,q_auto,w_800/v1741779867/11-sur-355_-_1_cxkczv.jpg",

    },
    {
      src: "https://res.cloudinary.com/dx5y2bzdq/image/upload/f_auto,q_auto,w_800/v1741779868/201-sur-355_q4pad4.jpg",
    },
    {
      src: "https://res.cloudinary.com/dx5y2bzdq/image/upload/f_auto,q_auto,w_800/v1741779869/4-sur-355_ypzyer.jpg",
    },
    {
      src: "https://res.cloudinary.com/dx5y2bzdq/image/upload/f_auto,q_auto,w_800/v1741779871/323-sur-355_-_2_oykjij.jpg",
    },
    {
      src: "https://res.cloudinary.com/dx5y2bzdq/image/upload/f_auto,q_auto,w_800/v1741779871/134-sur-355_etciph.jpg",
    }
  ];


  // Preload images - useRef to track loaded images
  const loadedImagesRef = useRef(new Set());

  const preloadImages = (index) => {
    const nextIndex = (index + 1) % galleryImages.length;
    const prevIndex = (index - 1 + galleryImages.length) % galleryImages.length;

    [nextIndex, prevIndex].forEach((i) => {
      if (!loadedImagesRef.current.has(i)) {
        const img = new Image();
        img.src = transformUrl(galleryImages[i].src);
        img.onload = () => loadedImagesRef.current.add(i);
      }
    });
  };

  useEffect(() => {
    preloadImages(activeImage);
  }, [activeImage]);

  const handleImageChange = (direction) => {
    setIsImageLoading(true);
    const newIndex = direction === 'next'
      ? (activeImage + 1) % galleryImages.length
      : (activeImage - 1 + galleryImages.length) % galleryImages.length;
    setActiveImage(newIndex);
  };

  const handleDotClick = (index) => {
    if (index !== activeImage) {
      setIsImageLoading(true);
      setActiveImage(index);
    }
  };

  const eventDetails = [
    {
      icon: <Calendar className="w-5 h-5" />,
      label: "Date",
      primary: "31 January 2025"
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      label: "Location",
      primary: "Four Seasons Hotel"
    },
    {
      icon: <Users className="w-5 h-5" />,
      label: "Guests",
      primary: "250 Persons"
    }
  ];


  return (
    <section className="bg-gradient-to-b from-gray-50 to-white py-16 md:py-24 overflow-hidden" id="about">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl text-gray-900 tracking-tight font-serif italic relative inline-block">
            {t('Our Last Project')}
            <motion.span 
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gray-900 to-transparent origin-left"
            />
          </h2>
        </motion.div>

        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Image Gallery */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:w-1/2 w-full relative group"
          >
            <div className="relative w-full h-[600px] sm:h-[500px] md:h-[600px] lg:h-[600px] overflow-hidden rounded-3xl shadow-2xl border border-gray-200/50 transition-all duration-700 ease-out group-hover:scale-[1.02] group-hover:shadow-3xl">
              {/* Low Quality Placeholder */}
              <img
                src={transformUrl(galleryImages[activeImage].src, 'thumbnail')}
                alt="Loading..."
                className="absolute inset-0 w-full h-full object-cover blur-sm transition-opacity duration-500"
                style={{
                  opacity: isImageLoading ? 1 : 0
                }}
              />

              {/* High Quality Image */}
              <motion.img
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                src={transformUrl(galleryImages[activeImage].src)}
                className="absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-in-out group-hover:scale-105"
                style={{
                  opacity: isImageLoading ? 0 : 1
                }}
                onLoad={() => {
                  setIsImageLoading(false);
                  loadedImagesRef.current.add(activeImage);
                }}
                loading="lazy"
                decoding="async"
                fetchPriority={activeImage === 0 ? "high" : "auto"}
              />

              {/* Gallery Navigation Buttons */}
              <div className="absolute inset-0 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-all duration-500">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleImageChange('prev')}
                  className="ml-4 sm:ml-8 p-3 sm:p-4 bg-white/90 backdrop-blur-md rounded-full shadow-2xl transform transition-all duration-500 -translate-x-16 group-hover:translate-x-0 hover:bg-white"
                  aria-label="Previous image"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleImageChange('next')}
                  className="mr-4 sm:mr-8 p-3 sm:p-4 bg-white/90 backdrop-blur-md rounded-full shadow-2xl transform transition-all duration-500 translate-x-16 group-hover:translate-x-0 hover:bg-white"
                  aria-label="Next image"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </motion.button>
              </div>

              {/* Gallery Dots */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 0.7, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="absolute bottom-6 left-0 right-0 flex justify-center items-center gap-2 transition-opacity duration-500 group-hover:opacity-100"
              >
                <div className="bg-white/80 backdrop-blur-md py-2 px-4 rounded-full flex items-center space-x-2 shadow-lg">
                  {galleryImages.map((_, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDotClick(index)}
                      aria-label={`View image ${index + 1}`}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${index === activeImage ? 'bg-gray-900 w-3 h-3' : 'bg-gray-400 hover:bg-gray-600'}`}
                    />
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Event Details */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:w-1/2 w-full space-y-8"
          >
            <div className="space-y-3">
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-4xl sm:text-5xl md:text-6xl font-serif text-gray-900 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700"
              >
                {t('Porsche Macan Electric')}
              </motion.h2>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-xl sm:text-2xl font-light text-gray-600 italic"
              >
                Reveal Event
              </motion.p>
            </div>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="text-base sm:text-lg text-gray-600 leading-relaxed mt-6"
            >
              {t('Step into the future of automotive luxury at the exclusive Porsche Macan Electric Reveal Event. This one-of-a-kind event brings together the perfect fusion of advanced technology and high-end design, showcasing the innovative features of the Porsche Macan Electric, a bold new step in the world of electric vehicles.')}
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              viewport={{ once: true }}
              className="mt-10 transform hover:scale-[1.01] transition-all duration-500"
            >
              <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-3xl p-6 sm:p-8 border border-gray-200/50 shadow-xl backdrop-blur-sm">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {eventDetails.map((detail, index) => (
                    <motion.div 
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center space-x-4 group cursor-pointer"
                    >
                      <motion.div 
                        whileHover={{ rotate: 5 }}
                        className="flex items-center justify-center w-14 h-14 bg-white rounded-2xl shadow-md border border-gray-100/50"
                      >
                        <motion.div 
                          whileHover={{ scale: 1.2 }}
                          className="text-gray-600 group-hover:text-gray-900 transition-colors duration-300"
                        >
                          {detail.icon}
                        </motion.div>
                      </motion.div>
                      <div className="flex flex-col">
                        <p className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider font-medium">{detail.label}</p>
                        <p className="text-sm sm:text-base text-gray-700 font-medium">{detail.primary}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Intro;